import { useMemo } from "react";
import className from "classnames/bind";
import { Button, RhLink } from "@/components";
import { getLinkData } from "@/core/utils";
import { Counter } from "@/components/FormComponents";
import { useTicketing } from "../../TicketingProvider";
import { TicketingFragment } from "@/__generated__/graphql";

import styles from "../TicketingForm.module.scss";
const cx = className.bind(styles);

type Ticket = TicketingFragment['ticketingForm']['step3Tickets']['nodes'][number];

type TicketProps = {
  ticket: Ticket;
  disabled?: boolean;
}

export default function TicketRow({ ticket, disabled }: TicketProps) {
  if (ticket.__typename !== "TicketPrice") return null;
  const {
    title,
    ticketPriceFields: {
      groupTicketPrice,
      price,
      ticketing: {
        cta,
        ticketureId,
        description,
        ticketingType,
        title: pricingTitle,
        membersTicketLink,
      }
    }
  } = ticket;
  const { selectedTickets, updateTickets, groupPricing } = useTicketing();
  const groupTicket = groupTicketPrice?.node?.__typename === "TicketPrice" ? groupTicketPrice.node : null;
  const ctaData = cta ? getLinkData(cta) : null;
  const generalTicketing = ticketingType?.[0] === "general";
  const memberLinkData = membersTicketLink ? getLinkData(membersTicketLink) : null;
  const rowTitle = useMemo(() => {
    if (groupPricing && groupTicket) {
      return groupTicket.ticketPriceFields?.ticketing?.title || groupTicket.title;
    }
    return pricingTitle || title;
  }, [groupPricing]);
  const rowPrice = useMemo(() => {
    if (groupPricing && groupTicket) {
      return groupTicket.ticketPriceFields?.price || price;
    }
    return price;
  }, [groupPricing]);
  const onIncrement = () => {
    updateTickets(ticketureId, (selectedTickets[ticketureId] || 0) + 1);
  };
  const onDecrement = () => {
    updateTickets(ticketureId, (selectedTickets[ticketureId] || 0) - 1);
  };
  return (
    <div className={cx("ticket")}>
      <div className={cx("ticket__copy")}>
        <p className={cx(["name", "text-m"])}>{rowTitle}</p>
        { description && <p className={cx(["description", "text-s"])}>{description}</p> }
        { ctaData && (
          <RhLink
            className={cx(["inline-link", "link"])}
            href={ctaData.url}
            target={ctaData.target}
            data-gtm-section="ticket-form"
            tabIndex={disabled ? -1 : 0}
          >
            {ctaData.label}
          </RhLink>
        )}
      </div>
      <div className={cx("ticket__content")}>
        <p className={cx(["price", "text-m"])}>{rowPrice}</p>
        { generalTicketing && (
          <Counter
            disabled={disabled}
            count={selectedTickets[ticketureId] || 0}
            onIncrement={onIncrement}
            onDecrement={onDecrement}
          />
        )}
        {memberLinkData && <Button className={cx("member-link")} data-gtm-section="ticket-form" { ...memberLinkData } noIcon /> }
      </div>
    </div>
  );
};